import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import Measure from "react-measure";

import {
  DownTrendIcon,
  NormalTrendIcon,
  UpTrendIcon,
} from "../../../assets/Icons";
import { Trend } from "../../../types";
import { Box, Flex } from "@chakra-ui/react";
import { useColorToken } from "../../../hooks/useColorToken";

/*
 * レイアウトパーツ：「いい調子」「普段通り」「疲れ気味」
 */
function TrendIcon(
  props: Readonly<{
    height: number;
    trend: Trend;
  }>,
): ReactElement {
  const size = props.height / 3.5;
  if (props.trend === Trend.Down) {
    // 「疲れ気味」の矢印
    return <DownTrendIcon height={size} width={size} />;
  } else if (props.trend === Trend.Normal) {
    // 「普段通り」の矢印
    return <NormalTrendIcon height={size} width={size} />;
  } else {
    // 「いい調子」の矢印
    return <UpTrendIcon height={size} width={size} />;
  }
}

/**
 * 大ゲージ
 * ホーム画面などで使う
 * @param props
 * @constructor
 */
export function LargeRadialChart(
  props: Readonly<{
    progress: number;
    barColor: string;
    title: string;
    score: string;
    trend?: Trend;
  }>,
): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const commonBase = useColorToken("common.base");
  const textMainLv1 = useColorToken("text.main_text_lv1");
  const drawBar = useCallback((): void => {
    const ctx = canvasRef.current?.getContext("2d") || null;
    if (!ctx) return;

    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;

    // スケール前にクリア（物理ピクセルでクリア）
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // 高精細化のためのスケーリング（SCALE_FACTOR=2を想定）
    const SCALE_FACTOR = 2;
    ctx.save();
    ctx.scale(SCALE_FACTOR, SCALE_FACTOR);

    ctx.lineCap = "round";

    // 以降の描画は論理ピクセルで行う（スケール適用済み）
    const logicalWidth = canvasWidth / SCALE_FACTOR;
    const logicalHeight = canvasHeight / SCALE_FACTOR;
    const centerX = logicalWidth / 2;
    const centerY = logicalHeight * 0.9;

    const radius =
      logicalHeight <= logicalWidth ? logicalHeight * 0.8 : logicalWidth * 0.4;
    const barWidth =
      logicalHeight <= logicalWidth ? logicalHeight / 12 : logicalWidth / 24;
    const fillColor = textMainLv1;
    const capFillColor = commonBase;
    drawGauge(
      ctx,
      centerX,
      centerY,
      radius,
      barWidth,
      props.barColor,
      props.progress,
      capFillColor,
    );

    ctx.canvas.style.letterSpacing = `0px`;
    // メーター表示内の文字色はChakraのextendThemeが反映されないため、直指定
    ctx.fillStyle = fillColor;

    ctx.font = `500 ${logicalHeight / 12}pt 'Noto Sans JP', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // 「心の活量値」「元気圧」のタイトル表示
    ctx.fillText(props.title, centerX, centerY - radius / 1.4);

    const moveLeft = props.trend != null ? radius / 5 : 0;
    const scoreFontSize =
      props.score.length > 2 ? logicalHeight / 3.2 : logicalHeight / 2.8;
    ctx.font = `500 ${scoreFontSize}pt 'Oswald', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // 「心の活量値」「元気圧」のスコア表示
    ctx.fillText(
      props.score.toString(),
      centerX - moveLeft,
      centerY - radius / 4,
    );
    ctx.lineCap = "round";

    ctx.restore(); // スケールをリセット
  }, [
    props.barColor,
    props.progress,
    props.title,
    props.score,
    props.trend,
    commonBase,
    textMainLv1,
  ]);

  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0) {
      drawBar();
    }
  }, [canvasSize, drawBar]);

  return (
    <Measure
      bounds
      onResize={(contentRect) => {
        const ASPECT_RATIO = 1 / 2;
        const SCALE_FACTOR = 2;
        const { width = 0 } = contentRect.bounds ?? {};
        const displayWidth = width;
        const canvasWidth = displayWidth * SCALE_FACTOR;
        const canvasHeight =
          Math.round(displayWidth * ASPECT_RATIO) * SCALE_FACTOR;

        if (
          canvasWidth !== canvasSize.width ||
          canvasHeight !== canvasSize.height
        ) {
          setCanvasSize({ width: canvasWidth, height: canvasHeight });
        }
      }}
    >
      {({ measureRef, contentRect }) => {
        const ASPECT_RATIO = 1 / 2;
        const { width = 0 } = contentRect.bounds ?? {};
        const displayWidth = width;
        const displayHeight = Math.round(width * ASPECT_RATIO);

        return (
          <Flex
            ref={measureRef}
            justifyContent="center"
            w="full"
            h="full"
            position="relative"
          >
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              style={{
                width: `${displayWidth}px`,
                height: `${displayHeight}px`,
              }}
            />
            {props.score !== "--" && props.trend != null && (
              <Box
                position="absolute"
                bottom={displayHeight * 0.05}
                right={displayHeight / 2}
              >
                <TrendIcon height={displayHeight} trend={props.trend} />
              </Box>
            )}
          </Flex>
        );
      }}
    </Measure>
  );
}

function drawGauge(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radius: number,
  barWidth: number,
  color: string,
  progress: number,
  capFillColor: string,
): void {
  const counterclockwise = false;

  const capCircleX = centerX + Math.cos(Math.PI * (1 + progress)) * radius;
  const capCircleY = centerY + Math.sin(Math.PI * (1 + progress)) * radius;

  ctx.moveTo(centerX - radius, centerY);
  ctx.lineWidth = barWidth;
  ctx.strokeStyle = `${color}99`;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, Math.PI, 0, counterclockwise);
  ctx.stroke();
  ctx.moveTo(centerX + radius, centerY);
  ctx.strokeStyle = `${color}`;
  ctx.beginPath();
  ctx.arc(
    centerX,
    centerY,
    radius,
    Math.PI,
    Math.PI * (1 + progress),
    counterclockwise,
  );
  ctx.stroke();

  ctx.fillStyle = capFillColor;
  ctx.beginPath();
  ctx.arc(capCircleX, capCircleY, barWidth / 4, 0, Math.PI * 2, true);
  ctx.fill();
}
